import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  DashboardSectionSkeleton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PaneListEmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  formatRelativeTimeIso,
  navigateWithThreadPreservation,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useRolesBasePath } from '../../lib/roles-base-path'
import { Archive, ChevronDown, ChevronUp, Copy, ListChecks, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  ROLE_SENIORITY_OPTIONS,
  roleSeniorityLabel,
  roleStatusBadgeVariant,
  roleStatusLabel,
  useArchiveRole,
  useDuplicateRole,
  useRemoveRoleCompetency,
  useReorderRoleCompetencies,
  useRole,
  useUpdateRoleCompetency,
  type RoleCompetency,
  type RoleDetail,
  type RoleSeniority,
} from '../../hooks/use-roles'
import { BiasCoachBanner, FeedbackBiasSection } from '../bias/bias-coach-banner'
import { useBiasReport } from '../../hooks/use-bias-review'
import { competencyFrameworkText, detect } from '../../lib/bias-guardrail'
import { BriefSectionEditAction } from './brief-section-edit-action'
import { fieldLabel, FRAMEWORK_COPY } from '../../lib/competency-framework'
import { RoleCanvasForm } from './role-canvas-form'
import { RoleCompetencyCanvas, TargetLevelSelect } from './role-competency-canvas'

function RoleCompetencyRow({
  role,
  rc,
  index,
}: {
  role: RoleDetail
  rc: RoleCompetency
  index: number
}) {
  const update = useUpdateRoleCompetency()
  const remove = useRemoveRoleCompetency()
  const reorder = useReorderRoleCompetencies()
  const onError = (error: Error) => toast.error(error.message || 'Failed to update competency')

  const move = (direction: -1 | 1) => {
    const ids = role.competencies.map((row) => row.id)
    const next = index + direction
    if (next < 0 || next >= ids.length) return
    ;[ids[index], ids[next]] = [ids[next], ids[index]]
    reorder.mutate({ roleId: role.id, orderedIds: ids }, { onError })
  }

  return (
    <li className="space-y-2 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="font-medium text-foreground">{rc.competency.label}</h3>
          {rc.competency.definition ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{rc.competency.definition}</p>
          ) : null}
          {rc.source_excerpt ? (
            <p className="text-sm text-foreground">
              <span className="text-muted-foreground">{FRAMEWORK_COPY.sourceLabel}</span>
              {rc.source_field ? ` · ${fieldLabel(rc.source_field)}` : ''}: {rc.source_excerpt}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={FRAMEWORK_COPY.moveUpLabel}
            disabled={index === 0 || reorder.isPending}
            onClick={() => move(-1)}
          >
            <ChevronUp aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={FRAMEWORK_COPY.moveDownLabel}
            disabled={index === role.competencies.length - 1 || reorder.isPending}
            onClick={() => move(1)}
          >
            <ChevronDown aria-hidden />
          </Button>
          <div className="w-44">
            <TargetLevelSelect
              ariaLabel={`Target level for ${rc.competency.label}`}
              levels={rc.competency.levels}
              value={rc.target_level}
              disabled={update.isPending}
              onChange={(target_level) =>
                update.mutate({ roleId: role.id, rcId: rc.id, changes: { target_level } }, { onError })
              }
            />
          </div>
          <Button
            type="button"
            variant="ghostDestructive"
            size="iconSm"
            aria-label={`Remove ${rc.competency.label}`}
            disabled={remove.isPending}
            onClick={() => remove.mutate({ roleId: role.id, rcId: rc.id }, { onError })}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      </div>
      {rc.rationale && !rc.source_excerpt ? <p className="text-sm text-foreground">{rc.rationale}</p> : null}
      {rc.competency.skills.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {rc.competency.skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
    </li>
  )
}

function DuplicateRoleDialog({
  role,
  open,
  onOpenChange,
  onDuplicated,
}: {
  role: RoleDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onDuplicated: (role: RoleDetail) => void
}) {
  const duplicate = useDuplicateRole()
  const [seniority, setSeniority] = useState<RoleSeniority>('')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate role</DialogTitle>
          <DialogDescription>
            Copies the competencies and target levels into a new draft role in the same family.
          </DialogDescription>
        </DialogHeader>
        <FormField label="Seniority of the copy" htmlFor="duplicate-role-seniority">
          <Select value={seniority} onValueChange={(value) => setSeniority(value as RoleSeniority)}>
            <SelectTrigger id="duplicate-role-seniority">
              <SelectValue placeholder="Choose seniority" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_SENIORITY_OPTIONS.filter((o) => o.value !== role.seniority).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!seniority || duplicate.isPending}
            onClick={() =>
              duplicate.mutate(
                { id: role.id, seniority },
                {
                  onSuccess: onDuplicated,
                  onError: (error: Error) => toast.error(error.message || 'Failed to duplicate role'),
                },
              )
            }
          >
            {duplicate.isPending ? 'Duplicating…' : 'Duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function RoleDetailPage() {
  const { roleId } = useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const rolesPath = useRolesBasePath()
  const [searchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { role, isLoading, error } = useRole(roleId)
  const archive = useArchiveRole()
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const frameworkFindings = useMemo(
    () => detect(competencyFrameworkText(role ?? {}), 'competency_framework'),
    [role],
  )
  const biasReport = useBiasReport([role?.id], 'competency_framework')

  useEffect(() => () => closeCanvas(), [closeCanvas])

  const goTo = useCallback(
    (path: string) => navigateWithThreadPreservation(navigate, path, searchParams),
    [navigate, searchParams],
  )

  const openEditCanvas = useCallback(() => {
    if (!role) return
    openCanvas({
      title: 'Edit role',
      preventAutoFocus: true,
      content: <RoleCanvasForm key={role.updated_at} role={role} onClose={closeCanvas} onSaved={closeCanvas} />,
    })
  }, [closeCanvas, openCanvas, role])

  const openAddCompetency = useCallback(() => {
    if (!role) return
    openCanvas({
      title: 'Add competency',
      preventAutoFocus: true,
      content: <RoleCompetencyCanvas role={role} onClose={closeCanvas} onSaved={closeCanvas} />,
    })
  }, [closeCanvas, openCanvas, role])

  if (isLoading) {
    return (
      <PageContainer>
        <DashboardSectionSkeleton cards={2} />
      </PageContainer>
    )
  }

  if (error || !role) {
    return (
      <PageContainer>
        <p className="py-12 text-center text-destructive">{(error as Error | null)?.message || 'Role not found'}</p>
      </PageContainer>
    )
  }

  const seniority = roleSeniorityLabel(role.seniority)
  const facts = [role.family, role.department].filter(Boolean)

  return (
    <PageContainer>
      <PageHeaderStack compact>
        <PageHeader leading={<PageHeaderLeading back={{ label: 'Back to roles', to: rolesPath }} />} />
      </PageHeaderStack>

      <div className="space-y-6">
        <Card className="group/section relative z-0">
          <CardHeader showTitle={false}>
            <BriefSectionEditAction label="Edit role details" onClick={openEditCanvas} />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {seniority ? <Badge variant="defaultSoft">{seniority}</Badge> : null}
                <Badge variant={roleStatusBadgeVariant(role.status)}>{roleStatusLabel(role.status)}</Badge>
              </div>
              <h1 className="text-xl font-semibold leading-tight text-foreground">{role.title}</h1>
            </div>
            {role.summary ? <p className="text-base text-foreground">{role.summary}</p> : null}
            <p className="text-sm text-muted-foreground">
              {[...facts, `Updated ${formatRelativeTimeIso(role.updated_at)}`].join(' · ')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setDuplicateOpen(true)}>
                <Copy aria-hidden />
                Duplicate for another seniority
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Competencies</CardTitle>
            <CardAction>
              <Button type="button" variant="outline" size="sm" onClick={openAddCompetency}>
                <Plus aria-hidden />
                Add competency
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent className="space-y-3">
            <BiasCoachBanner findings={frameworkFindings} source="edit" />
            <FeedbackBiasSection
              open={biasReport.data?.open ?? frameworkFindings}
              incidentCount={biasReport.data?.incident_count ?? 0}
            />
            {role.competencies.length === 0 ? (
              <PaneListEmptyState
                icon={<ListChecks aria-hidden />}
                title="No competencies yet"
                description="Add competencies from the shared library and set the level this role needs"
              />
            ) : (
              <ul className="divide-y divide-border">
                {role.competencies.map((rc, index) => (
                  <RoleCompetencyRow key={rc.id} role={role} rc={rc} index={index} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {role.status !== 'archived' ? (
          <div className="flex justify-end">
            <Button type="button" variant="outlineDestructive" size="sm" onClick={() => setArchiveOpen(true)}>
              <Archive aria-hidden />
              Archive
            </Button>
          </div>
        ) : null}
      </div>

      <DuplicateRoleDialog
        role={role}
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        onDuplicated={(copy) => {
          setDuplicateOpen(false)
          toast.success(`Created ${copy.title}`)
          goTo(`${rolesPath}/${copy.id}`)
        }}
      />

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive role</AlertDialogTitle>
            <AlertDialogDescription>
              The role leaves the Roles list. Its competencies stay in the shared library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={archive.isPending}
              onClick={() =>
                archive.mutate(role.id, {
                  onSuccess: () => {
                    toast.success('Role archived')
                    goTo(rolesPath)
                  },
                  onError: (e: Error) => toast.error(e.message || 'Failed to archive role'),
                })
              }
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
